import { useState, useEffect, useCallback } from "react";
import api from "../api";
import { Plus, Pencil, Trash2, Flag, MessageSquare, Activity } from "lucide-react";

const ACOES = {
  criar:   { t:"Criou",     c:"#1D9E75", Icon:Plus },
  editar:  { t:"Editou",    c:"#579bfc", Icon:Pencil },
  excluir: { t:"Excluiu",   c:"#E24B4A", Icon:Trash2 },
  status:  { t:"Mudou status de", c:"#EBA13A", Icon:Flag },
  contato: { t:"Registrou contato em", c:"#a25ddc", Icon:MessageSquare },
};

const inp = { width:"100%", padding:"8px 10px", border:"1px solid var(--border-color)", borderRadius:6, fontSize:13, outline:"none", boxSizing:"border-box" };
const lbl = { display:"block", fontSize:11, fontWeight:700, color:"var(--text-secondary)", textTransform:"uppercase", letterSpacing:.4, marginBottom:4 };

function quando(d) {
  const dt = new Date(d);
  const agora = new Date();
  const dif = Math.floor((agora - dt) / 1000);
  if (dif < 60) return "agora";
  if (dif < 3600) return `${Math.floor(dif/60)} min atrás`;
  if (dif < 86400) return `${Math.floor(dif/3600)}h atrás`;
  if (dif < 172800) return "ontem";
  return dt.toLocaleDateString("pt-BR");
}
function hora(d) {
  return new Date(d).toLocaleTimeString("pt-BR", { hour:"2-digit", minute:"2-digit" });
}
function diaLabel(d) {
  const dt = new Date(d);
  const hoje = new Date();
  const ontem = new Date(); ontem.setDate(hoje.getDate() - 1);
  const iso = (x) => x.toISOString().slice(0,10);
  if (iso(dt) === iso(hoje)) return "Hoje";
  if (iso(dt) === iso(ontem)) return "Ontem";
  return dt.toLocaleDateString("pt-BR", { weekday:"long", day:"2-digit", month:"long" });
}

function Avatar({ nome }) {
  const iniciais = String(nome || "?").split(" ").slice(0,2).map(p => p[0]).join("").toUpperCase();
  const cores = ["#EE6A1A","#579bfc","#1D9E75","#a25ddc","#EBA13A","#E24B4A"];
  const cor = cores[(nome || "").length % cores.length];
  return (
    <div style={{ width:30, height:30, borderRadius:"50%", background:cor+"22", color:cor, display:"flex", alignItems:"center", justifyContent:"center", fontSize:11, fontWeight:800, flexShrink:0 }}>
      {iniciais}
    </div>
  );
}

export default function Historico({ currentUser }) {
  const [logs, setLogs] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [fUsuario, setFUsuario] = useState("");
  const [fEntidade, setFEntidade] = useState("");
  const [fAcao, setFAcao] = useState("");
  const [de, setDe] = useState("");
  const [ate, setAte] = useState("");
  const [load, setLoad] = useState(false);

  const carregar = useCallback(async () => {
    setLoad(true);
    try {
      const p = new URLSearchParams();
      if (fUsuario)  p.set("usuario", fUsuario);
      if (fEntidade) p.set("entidade", fEntidade);
      if (fAcao)     p.set("acao", fAcao);
      if (de)        p.set("de", de);
      if (ate)       p.set("ate", ate);
      const [rL, rU] = await Promise.all([
        api.get(`/logs?${p.toString()}`),
        api.get("/users"),
      ]);
      setLogs(Array.isArray(rL.data) ? rL.data : []);
      setUsuarios(Array.isArray(rU.data) ? rU.data : (rU.data?.users || []));
    } catch (e) { console.error("Erro ao carregar logs", e); }
    setLoad(false);
  }, [fUsuario, fEntidade, fAcao, de, ate]);

  useEffect(() => { carregar(); }, [carregar]);

  const entidades = [...new Set(logs.map(l => l.entidade).filter(Boolean))].sort();

  // agrupa por dia
  const grupos = [];
  logs.forEach(l => {
    const dia = new Date(l.createdAt).toISOString().slice(0,10);
    let g = grupos.find(x => x.dia === dia);
    if (!g) { g = { dia, data: l.createdAt, itens: [] }; grupos.push(g); }
    g.itens.push(l);
  });

  const contar = (a) => logs.filter(l => l.acao === a).length;

  return (
    <div style={{ padding:"24px 28px", background:"var(--page-bg)", minHeight:"100vh" }}>
      <div style={{ marginBottom:20 }}>
        <h1 style={{ fontSize:26, fontWeight:800, color:"var(--text-primary)", margin:0 }}>Histórico</h1>
        <p style={{ fontSize:13, color:"var(--text-secondary)", margin:"4px 0 0" }}>Tudo o que acontece no CRM, por usuário</p>
      </div>

      <div style={{ display:"flex", gap:10, marginBottom:16, flexWrap:"wrap" }}>
        {Object.keys(ACOES).map(k => {
          const A = ACOES[k];
          const ativo = fAcao === k;
          return (
            <div key={k} onClick={() => setFAcao(ativo ? "" : k)}
              style={{ flex:1, minWidth:130, padding:"12px 14px", borderRadius:10, background:A.c+"0F", border:`1px solid ${ativo ? A.c : A.c+"33"}`, cursor:"pointer", display:"flex", alignItems:"center", gap:10 }}>
              <A.Icon size={16} color={A.c} />
              <div>
                <div style={{ fontSize:18, fontWeight:700, color:A.c, lineHeight:1 }}>{contar(k)}</div>
                <div style={{ fontSize:10, fontWeight:700, color:"var(--text-secondary)", textTransform:"uppercase", letterSpacing:.4, marginTop:3 }}>{k}</div>
              </div>
            </div>
          );
        })}
      </div>

      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr 1fr auto", gap:10, marginBottom:16, background:"var(--card-bg)", padding:14, borderRadius:10, border:"1px solid var(--border-color)", alignItems:"end" }}>
        <div>
          <label style={lbl}>Usuário</label>
          <select style={inp} value={fUsuario} onChange={e => setFUsuario(e.target.value)}>
            <option value="">Todos</option>
            {usuarios.map(u => <option key={u._id} value={u._id}>{u.nome}</option>)}
          </select>
        </div>
        <div>
          <label style={lbl}>Módulo</label>
          <select style={inp} value={fEntidade} onChange={e => setFEntidade(e.target.value)}>
            <option value="">Todos</option>
            {entidades.map(e => <option key={e} value={e}>{e}</option>)}
          </select>
        </div>
        <div>
          <label style={lbl}>De</label>
          <input type="date" style={inp} value={de} onChange={e => setDe(e.target.value)} />
        </div>
        <div>
          <label style={lbl}>Até</label>
          <input type="date" style={inp} value={ate} onChange={e => setAte(e.target.value)} />
        </div>
        <button
          onClick={() => { setFUsuario(""); setFEntidade(""); setFAcao(""); setDe(""); setAte(""); }}
          style={{ padding:"8px 14px", fontSize:12, fontWeight:600, color:"var(--text-secondary)", background:"transparent", border:"1px solid var(--border-color)", borderRadius:6, cursor:"pointer", height:36 }}>
          Limpar
        </button>
      </div>

      <div style={{ background:"var(--card-bg)", borderRadius:10, border:"1px solid var(--border-color)", padding:"8px 0" }}>
        {load && <div style={{ padding:30, textAlign:"center", color:"#98A2B3", fontSize:13 }}>Carregando...</div>}

        {!load && !logs.length && (
          <div style={{ padding:40, textAlign:"center", color:"#98A2B3" }}>
            <Activity size={28} style={{ opacity:.4, marginBottom:8 }} />
            <div style={{ fontSize:13 }}>Nenhuma atividade registrada neste período.</div>
          </div>
        )}

        {grupos.map(g => (
          <div key={g.dia}>
            <div style={{ padding:"10px 18px", background:"var(--page-bg)", borderTop:"1px solid var(--border-light)", borderBottom:"1px solid var(--border-light)", fontSize:11, fontWeight:800, color:"var(--text-secondary)", textTransform:"uppercase", letterSpacing:.5, position:"sticky", top:0 }}>
              {diaLabel(g.data)} <span style={{ color:"#98A2B3", fontWeight:600 }}>· {g.itens.length}</span>
            </div>
            {g.itens.map(l => {
              const A = ACOES[l.acao] || ACOES.editar;
              return (
                <div key={l._id} style={{ display:"flex", alignItems:"center", gap:12, padding:"11px 18px", borderBottom:"1px solid var(--page-bg)" }}>
                  <Avatar nome={l.nomeUsuario} />
                  <div style={{ width:26, height:26, borderRadius:6, background:A.c+"18", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                    <A.Icon size={13} color={A.c} />
                  </div>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontSize:13, color:"var(--text-secondary)" }}>
                      <strong style={{ color:"var(--text-primary)" }}>{l.nomeUsuario || "Sistema"}</strong>
                      {" "}
                      <span style={{ color:A.c, fontWeight:600 }}>{A.t.toLowerCase()}</span>
                      {" "}
                      <span style={{ color:"var(--text-secondary)" }}>{l.entidade}</span>
                      {l.titulo && (
                        <>
                          {" "}
                          <strong style={{ color:"var(--text-primary)" }}>{l.titulo}</strong>
                        </>
                      )}
                      {l.para && (
                        <span style={{ marginLeft:6, display:"inline-block", padding:"1px 8px", borderRadius:10, fontSize:11, fontWeight:600, color:A.c, background:A.c+"18" }}>
                          {l.para}
                        </span>
                      )}
                    </div>
                    {l.descricao && (
                      <div style={{ fontSize:11, color:"#98A2B3", marginTop:2, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                        {l.descricao}
                      </div>
                    )}
                  </div>
                  <div style={{ textAlign:"right", flexShrink:0 }}>
                    <div style={{ fontSize:12, color:"var(--text-secondary)", fontWeight:600 }}>{hora(l.createdAt)}</div>
                    <div style={{ fontSize:10, color:"#98A2B3" }}>{quando(l.createdAt)}</div>
                  </div>
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}
